/**
 * Hook to filter navigation/menu items based on feature flags
 *
 * Maps item keys to their corresponding feature flags and filters out
 * items whose features are disabled.
 */

import { useMemo } from "react";
import { useFeature } from "@/hooks/useFeature";

// Map navigation item keys to their feature flag names
const ITEM_TO_FEATURE_MAP: Record<string, string> = {
  // Entertainment items
  youtube: "youtube",
  tv: "tv",
  music: "music",
  radio: "radio",
  games: "games",
  // Home items
  help: "helpAlert",
  rest: "rest",
  communication: "communicationBoard",
  keyboard: "aacKeyboard",
  entertainment: "entertainment",
  // Control methods
  eyeControl: "eyeControl",
  headTracking: "headTracking",
  gazeTracking: "gazeTracking",
  mouthGestures: "mouthGestures",
  externalSwitch: "externalSwitch",
  voiceControl: "voiceControl",
  // Smart home
  smartHome: "smartHome",
  // Other common items
  contacts: "contacts",
  translator: "translator",
  aiChat: "aiChat",
  voiceCloning: "voiceCloning",
  nightMonitoring: "nightMonitoring",
  caregiverConnect: "caregiverConnect",
  emergency: "emergency",
};

/**
 * Get the feature flag name for a given item key
 */
export function getFeatureFlagForItem(itemKey: string): string | undefined {
  return ITEM_TO_FEATURE_MAP[itemKey];
}

/**
 * Hook that returns feature flag states for all navigation items
 */
export function useNavigationFeatureFlags() {
  // Get all feature states
  const youtube = useFeature("youtube");
  const tv = useFeature("tv");
  const music = useFeature("music");
  const radio = useFeature("radio");
  const games = useFeature("games");
  const helpAlert = useFeature("helpAlert");
  const rest = useFeature("rest");
  const communicationBoard = useFeature("communicationBoard");
  const aacKeyboard = useFeature("aacKeyboard");
  const entertainment = useFeature("entertainment");
  const eyeControl = useFeature("eyeControl");
  const headTracking = useFeature("headTracking");
  const gazeTracking = useFeature("gazeTracking");
  const mouthGestures = useFeature("mouthGestures");
  const externalSwitch = useFeature("externalSwitch");
  const voiceControl = useFeature("voiceControl");
  const smartHome = useFeature("smartHome");
  const contacts = useFeature("contacts");
  const translator = useFeature("translator");
  const aiChat = useFeature("aiChat");
  const voiceCloning = useFeature("voiceCloning");
  const nightMonitoring = useFeature("nightMonitoring");
  const caregiverConnect = useFeature("caregiverConnect");
  const emergency = useFeature("emergency");

  return useMemo(
    () => ({
      youtube,
      tv,
      music,
      radio,
      games,
      helpAlert,
      rest,
      communicationBoard,
      aacKeyboard,
      entertainment,
      eyeControl,
      headTracking,
      gazeTracking,
      mouthGestures,
      externalSwitch,
      voiceControl,
      smartHome,
      contacts,
      translator,
      aiChat,
      voiceCloning,
      nightMonitoring,
      caregiverConnect,
      emergency,
    }),
    [
      youtube,
      tv,
      music,
      radio,
      games,
      helpAlert,
      rest,
      communicationBoard,
      aacKeyboard,
      entertainment,
      eyeControl,
      headTracking,
      gazeTracking,
      mouthGestures,
      externalSwitch,
      voiceControl,
      smartHome,
      contacts,
      translator,
      aiChat,
      voiceCloning,
      nightMonitoring,
      caregiverConnect,
      emergency,
    ]
  );
}

/**
 * Check if an item is enabled based on its key
 */
export function useIsItemEnabled(itemKey: string): boolean {
  const featureFlags = useNavigationFeatureFlags();
  const featureName = ITEM_TO_FEATURE_MAP[itemKey];

  // If no feature flag mapping exists, item is enabled by default
  if (!featureName) return true;

  return featureFlags[featureName as keyof typeof featureFlags] !== false;
}

/**
 * Filter an array of items by their feature flags
 * Items must have a 'key' property that maps to a feature flag
 */
export function useFeatureFilteredItems<T extends { key: string }>(
  items: T[]
): T[] {
  const featureFlags = useNavigationFeatureFlags();

  return useMemo(() => {
    return items.filter((item) => {
      const featureName = ITEM_TO_FEATURE_MAP[item.key];
      // If no feature flag mapping exists, item is enabled by default
      if (!featureName) return true;
      return featureFlags[featureName as keyof typeof featureFlags] !== false;
    });
  }, [items, featureFlags]);
}
